<p-dialog
  [visible]="visible()"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  appendTo="body"
  [breakpoints]="{ '960px': '92vw', '640px': '100vw' }"
  [style]="{ width: 'min(42rem, 96vw)' }"
  contentStyleClass="overflow-y-auto p-2 md:p-3"
  [contentStyle]="{ 'max-height': 'min(70vh, 32rem)' }"
  (onHide)="onHide()"
  aria-label="client-booking-detail-dialog"
  data-testid="client-booking-detail-dialog"
>
  <ng-template pTemplate="header">
    <div class="flex align-items-center justify-content-between gap-2 w-full pr-4">
      <span class="text-base font-semibold" data-testid="booking-detail-dialog-title">
        {{ 'CLIENT_BOOKING.V2.DETAIL_TITLE' | translate }}
      </span>
      @if (!loading() && detail()) {
        <p-tag
          [value]="summaryStatus()"
          [severity]="summaryStatusSeverity()"
          styleClass="text-xxs uppercase white-space-nowrap"
          data-testid="booking-detail-header-status"
        />
      }
    </div>
  </ng-template>

  @if (loading()) {
    <div class="flex flex-column gap-3 py-2" data-testid="booking-detail-dialog-skeleton">
      <p-skeleton height="4rem" styleClass="w-full border-round" />
      @for (line of [1, 2, 3]; track line) {
        <p-skeleton height="1.25rem" styleClass="w-full" />
      }
    </div>
  } @else if (detail(); as bookingDetail) {
    <div
      class="flex flex-column gap-3 text-sm w-full"
      data-testid="booking-detail-dialog-content"
    >
      <section
        class="surface-50 border-1 border-200 border-round p-3 w-full"
        aria-label="booking-summary-section"
        data-testid="booking-detail-summary"
      >
        <div class="flex flex-column gap-2">
          <div class="flex flex-wrap align-items-start justify-content-between gap-2">
            <div class="flex-1 min-w-0">
              <div class="text-lg font-semibold line-height-3 word-break-all">
                {{ summaryTitle() || '—' }}
              </div>
              @if (summaryStartDatetime(); as start) {
                <div
                  class="text-600 mt-1 flex align-items-center gap-2 flex-wrap"
                  data-testid="booking-detail-summary-datetime"
                >
                  <i class="fa fa-calendar text-primary" aria-hidden="true"></i>
                  <span>{{ start | datetimeformat: 'medium' }}</span>
                </div>
              }
              @if (summaryCoachName()) {
                <div
                  class="text-600 mt-1 flex align-items-center gap-2"
                  data-testid="booking-detail-summary-coach"
                >
                  <i class="fa fa-user text-primary" aria-hidden="true"></i>
                  <span>{{ summaryCoachName() }}</span>
                </div>
              }
            </div>
            <p-tag
              [value]="
                (bookingKind() === 'class'
                  ? 'CLIENT_BOOKING.V2.CLASS_BOOKING'
                  : 'CLIENT_BOOKING.V2.APPOINTMENT_BOOKING'
                ) | translate
              "
              severity="secondary"
              styleClass="text-xxs uppercase"
            />
          </div>
          @if (headerBadges().length) {
            <div class="flex flex-wrap gap-1" data-testid="booking-detail-summary-badges">
              @for (badge of headerBadges(); track badge.labelKey) {
                <p-tag
                  [severity]="badge.severity"
                  styleClass="text-xxs"
                  [value]="badge.labelKey | translate"
                />
              }
            </div>
          }
        </div>
      </section>

      <p-divider class="m-0" />

      <section class="w-full" aria-label="medical-alert-section">
        <h3 class="text-600 text-xs font-semibold uppercase mb-2 m-0">
          {{ 'CLIENT_BOOKING.V2.MEDICAL_ALERT' | translate }}
        </h3>
        @if (bookingDetail.medical_alert?.trim()) {
          <p-message
            severity="warn"
            styleClass="w-full border-none"
            aria-label="medical-alert-message"
            data-testid="booking-detail-medical-alert"
          >
            <span class="text-sm">{{ bookingDetail.medical_alert }}</span>
          </p-message>
        } @else {
          <p
            class="text-500 m-0 p-2 border-round surface-50"
            data-testid="booking-detail-medical-alert-empty"
          >
            {{ 'CLIENT_BOOKING.V2.NO_MEDICAL_ALERT' | translate }}
          </p>
        }
      </section>

      <p-divider class="m-0" />

      <section class="w-full" aria-label="membership-section">
        <div class="flex align-items-center justify-content-between gap-2 flex-wrap mb-2">
          <h3 class="text-600 text-xs font-semibold uppercase m-0">
            {{ 'CLIENT_BOOKING.V2.MEMBERSHIP' | translate }}
          </h3>
          @if (hasMultipleMemberships()) {
            <button
              pButton
              pRipple
              type="button"
              class="p-button-text p-button-sm text-xs"
              [label]="
                (showAllMemberships()
                  ? 'CLIENT_BOOKING.V2.SHOW_SELECTED_ONLY'
                  : 'CLIENT_BOOKING.V2.SHOW_ALL_MEMBERSHIPS'
                ) | translate: { count: bookingDetail.memberships?.length ?? 0 }
              "
              (click)="toggleMemberships()"
              [attr.aria-expanded]="showAllMemberships()"
              aria-label="toggle-memberships-list"
              data-testid="toggle-memberships-list"
            ></button>
          }
        </div>

        @if (selectedMembership(); as selected) {
          <article
            class="p-3 border-1 border-primary border-round surface-50 w-full shadow-1"
            data-testid="booking-detail-selected-membership"
          >
            <div class="flex align-items-start justify-content-between gap-2 flex-wrap">
              <div class="flex-1 min-w-0">
                <div class="font-semibold word-break-all">
                  {{ membershipPrimaryLabel(selected) }}
                </div>
                @if (membershipSecondaryLabel(selected); as secondary) {
                  <div class="text-600 text-xs mt-1">{{ secondary }}</div>
                }
              </div>
              <p-tag
                severity="info"
                styleClass="text-xxs white-space-nowrap"
                [value]="'CLIENT_BOOKING.V2.SELECTED_MEMBERSHIP' | translate"
              />
            </div>
            <div
              class="flex flex-wrap gap-3 mt-2 text-xs text-600"
              data-testid="booking-detail-membership-stats"
            >
              <span>
                {{ 'CLIENT_BOOKING.V2.SESSIONS_AVAILABLE' | translate }}:
                <strong class="text-900">{{ membershipSessionsLabel(selected) }}</strong>
              </span>
              @if (selected.subscription_status) {
                <span>
                  {{ 'CLIENT_BOOKING.STATUS' | translate }}:
                  <strong class="text-900">{{ selected.subscription_status }}</strong>
                </span>
              }
            </div>
          </article>
        } @else if (!bookingDetail.memberships?.length) {
          <p
            class="text-500 m-0 p-2 border-round surface-50"
            data-testid="booking-detail-no-memberships"
          >
            {{ 'CLIENT_BOOKING.V2.NONE' | translate }}
          </p>
        }

        @if (showAllMemberships() && otherMemberships().length) {
          <div
            class="flex flex-column gap-2 mt-2 w-full"
            data-testid="booking-detail-other-memberships"
          >
            @for (membership of otherMemberships(); track membership.id) {
              <article
                class="p-3 border-1 border-300 border-round surface-0 w-full"
                [attr.data-testid]="'booking-detail-membership-' + membership.id"
              >
                <div class="font-medium word-break-all">
                  {{ membershipPrimaryLabel(membership) }}
                </div>
                @if (membershipSecondaryLabel(membership); as secondary) {
                  <div class="text-600 text-xs mt-1">{{ secondary }}</div>
                }
                <div class="text-xs text-600 mt-1">
                  {{ 'CLIENT_BOOKING.V2.SESSIONS_AVAILABLE' | translate }}:
                  {{ membershipSessionsLabel(membership) }}
                </div>
              </article>
            }
          </div>
        }
      </section>

      <p-divider class="m-0" />

      <section class="w-full" aria-label="booking-metadata-section">
        <h3 class="text-600 text-xs font-semibold uppercase mb-2 m-0">
          {{ 'CLIENT_BOOKING.V2.ADDITIONAL_DETAILS' | translate }}
        </h3>
        <div class="grid w-full m-0">
          @for (item of metaItems(); track item.testId) {
            <div
              class="col-12 md:col-6 lg:col-4 p-0 md:pr-2 mb-2"
              [attr.data-testid]="item.testId"
            >
              <div
                class="p-2 border-1 border-300 border-round surface-50 h-full w-full"
              >
                <div class="text-600 text-xs font-semibold uppercase mb-1">
                  {{ item.labelKey | translate }}
                </div>
                <div class="text-sm word-break-all">
                  @if (item.valueKey) {
                    <p-tag
                      [severity]="item.tagSeverity ?? 'secondary'"
                      styleClass="text-xxs"
                      [value]="item.valueKey | translate"
                    />
                  } @else if (isDateMetaItem(item)) {
                    {{ item.value | datetimeformat: 'medium' }}
                  } @else {
                    {{ item.value }}
                  }
                </div>
              </div>
            </div>
          }
        </div>
      </section>
    </div>
  }

  <ng-template pTemplate="footer">
    <div class="flex justify-content-end w-full">
      <button
        pButton
        pRipple
        type="button"
        class="p-button-outlined"
        [label]="'CLIENT_BOOKING.V2.CLOSE' | translate"
        (click)="onHide()"
        aria-label="close-booking-detail-dialog"
        data-testid="close-booking-detail-dialog"
      ></button>
    </div>
  </ng-template>
</p-dialog>
